import { useCallback, useEffect, useMemo, useState } from 'react';
import { Hash, ShieldCheck, X } from 'lucide-react';
import { COMMAND_OVERRIDE_LIMIT, type CommandOverride, type SpaceCommand } from '@spawnwire/shared';
import { Button, clsx, Select, Spinner } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { loadMembers } from '../../lib/data';
import { channelsOfSpace, displayNameIn, useStore } from '../../store/store';
import { ui } from '../../store/ui';
import { Section } from '../settings/SettingsLayout';
import { hasRoom, isOffIn, ruleState, ruleSummary, sameRules, withRule, type RuleState } from './ruleEdits';

interface Loaded {
  commands: SpaceCommand[];
  rules: Record<string, CommandOverride[]>;
}

const STATES: { value: RuleState; label: string }[] = [
  { value: 'default', label: 'Default' },
  { value: 'allow', label: 'Always allow' },
  { value: 'deny', label: 'Never allow' },
];

const commandLabel = (c: SpaceCommand) => (c.type === 'chat' ? `/${c.name}` : c.name);

/**
 * Space settings → Bots → "Who can use commands": for each command a bot added, which roles and people may always or never
 * use it, and which channels it is switched off in. Everyone else follows the command's own default, and administrators
 * and the owner can always use everything.
 */
export function CommandRules({ spaceId }: { spaceId: string }) {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState('');
  /** What is on screen for the picked command; kept apart from `loaded` until it is saved. */
  const [draft, setDraft] = useState<CommandOverride[]>([]);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState('');

  const space = useStore((s) => s.spaces[spaceId]);
  const allChannels = useStore((s) => s.channels);
  // Computed here, not inside the selector: a selector that returns a new list every time never settles.
  const channels = useMemo(
    () => channelsOfSpace({ channels: allChannels }, spaceId).filter((c) => c.type === 'text' || c.type === 'announcement' || c.type === 'forum' || c.type === 'media'),
    [allChannels, spaceId],
  );
  const members = useStore((s) => s.members[spaceId]);
  const everyone = useStore((s) => s.spaces[spaceId]?.roles.find((r) => r.isDefault));

  const load = useCallback(async () => {
    try {
      const res = await http.get<{ data: { commandId: string; overrides: CommandOverride[] }[]; commands: SpaceCommand[] }>(`/spaces/${spaceId}/command-permissions`);
      setLoaded({ commands: res.commands, rules: Object.fromEntries(res.data.map((d) => [d.commandId, d.overrides])) });
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [spaceId]);
  useEffect(() => {
    void load();
    void loadMembers(spaceId).catch(() => {});
  }, [load, spaceId]);

  // Picking a command (or the rules arriving again from the server) starts the draft from what is saved.
  const saved = picked && loaded ? (loaded.rules[picked] ?? []) : [];
  const savedKey = JSON.stringify(saved);
  useEffect(() => {
    setDraft(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the saved rules' content
  }, [picked, savedKey]);

  const command = loaded?.commands.find((c) => c.id === picked);
  const roles = useMemo(() => (space?.roles ?? []).filter((r) => !r.botId).sort((a, b) => b.position - a.position), [space]);
  const dirty = !sameRules(draft, saved);
  const people = draft.filter((o) => o.targetType === 'user');
  const addable = Object.values(members ?? {}).filter((m) => !m.user.bot && !draft.some((o) => o.targetType === 'user' && o.targetId === m.user.id));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await http.put(`/spaces/${spaceId}/commands/${picked}/permissions`, { overrides: draft });
      await load();
      ui.toast('Saved.', 'success');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (error && !loaded) return <p className="text-sm text-danger">{error}</p>;
  if (!loaded) return <Spinner />;
  if (loaded.commands.length === 0) return null;

  return (
    <Section
      title="Who can use commands"
      description="Bots decide what their commands do, but you decide who may use them here. Choose a command, then allow or block roles and people, or switch it off in some channels. Administrators and the owner can always use every command."
    >
      <label className="block text-xs font-semibold uppercase tracking-wide text-muted" htmlFor="cr-command">
        Command
      </label>
      <Select
        id="cr-command"
        value={picked}
        onChange={setPicked}
        options={[{ value: '', label: 'Choose a command…' }, ...loaded.commands.map((c) => ({ value: c.id, label: `${commandLabel(c)} · ${c.bot.displayName} · ${ruleSummary((loaded.rules[c.id] ?? []).length)}` }))]}
      />
      {command && (
        <div className="mt-4 space-y-5" data-testid="command-rules">
          {command.defaultMemberPermissions !== null && (
            <p className="rounded-md bg-active px-3 py-2 text-xs text-muted">
              This command asks for permissions by default, so only people who have them can use it. Allowing a role or person lifts that for them.
            </p>
          )}
          <div>
            <h4 className="mb-1 text-sm font-semibold text-fg">Roles</h4>
            <ul className="divide-y divide-line rounded-lg border border-line">
              {[...(everyone ? [everyone] : []), ...roles.filter((r) => !r.isDefault)].map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <span className="min-w-0 truncate text-sm text-fg">{r.isDefault ? '@everyone' : r.name}</span>
                  <Select<RuleState> className="!w-40" value={ruleState(draft, 'role', r.id)} options={STATES} onChange={(s) => setDraft((d) => withRule(d, 'role', r.id, s))} />
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="mb-1 text-sm font-semibold text-fg">People</h4>
            {people.length > 0 && (
              <ul className="mb-2 divide-y divide-line rounded-lg border border-line">
                {people.map((o) => (
                  <li key={o.targetId} className="flex items-center justify-between gap-3 px-3 py-2">
                    <span className="min-w-0 truncate text-sm text-fg">{displayNameIn(useStore.getState(), o.targetId, spaceId)}</span>
                    <span className="flex items-center gap-2">
                      <Select<RuleState> className="!w-40" value={ruleState(draft, 'user', o.targetId)} options={STATES.filter((s) => s.value !== 'default')} onChange={(s) => setDraft((d) => withRule(d, 'user', o.targetId, s))} />
                      <button type="button" aria-label="Remove this person's rule" className="rounded p-1 text-muted hover:bg-hover hover:text-fg" onClick={() => setDraft((d) => withRule(d, 'user', o.targetId, 'default'))}>
                        <X className="size-4" />
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <Select
              id="cr-add-person"
              value={adding}
              onChange={(id) => {
                if (id && hasRoom(draft)) setDraft((d) => withRule(d, 'user', id, 'deny'));
                setAdding('');
              }}
              options={[{ value: '', label: 'Add a person…' }, ...addable.map((m) => ({ value: m.user.id, label: m.nickname ?? m.user.displayName }))]}
            />
          </div>
          <div>
            <h4 className="mb-1 text-sm font-semibold text-fg">Channels</h4>
            <p className="mb-1 text-xs text-muted">Tick a channel to switch this command off there (and in its threads), for everyone but administrators.</p>
            <ul className="divide-y divide-line rounded-lg border border-line">
              {channels.map((c) => (
                <li key={c.id}>
                  <label className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-fg">
                    <input type="checkbox" checked={isOffIn(draft, c.id)} onChange={(e) => setDraft((d) => withRule(d, 'channel', c.id, e.target.checked ? 'deny' : 'default'))} className="size-4 accent-[var(--accent)]" />
                    <Hash className="size-4 text-muted" aria-hidden />
                    <span className="min-w-0 truncate">{c.name}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
          {!hasRoom(draft) && <p className="text-xs text-danger">A command can have at most {COMMAND_OVERRIDE_LIMIT} rules.</p>}
          {error && (
            <p role="alert" className="rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
              {error}
            </p>
          )}
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" disabled={!dirty || busy} onClick={() => setDraft(saved)}>
              Undo changes
            </Button>
            <Button icon={ShieldCheck} loading={busy} disabled={!dirty} onClick={() => void save()} className={clsx(!dirty && 'opacity-60')}>
              Save
            </Button>
          </div>
        </div>
      )}
    </Section>
  );
}
